import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { clearCart } = useCart();
  const [phase, setPhase] = useState("verifying"); // verifying | paid | timeout | invalid
  const [order, setOrder] = useState(null);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setPhase("invalid");
      return;
    }
    let attempts = 0;
    const maxAttempts = 20;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!clearedRef.current) {
            clearedRef.current = true;
            clearCart();
          }
          const orderRes = await axios.get(`${API}/orders/${sessionId}`);
          setOrder(orderRes.data);
          setPhase("paid");
          return;
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          setPhase("invalid");
          return;
        }
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        setPhase("timeout");
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div
      data-testid="payment-success-page"
      className="min-h-screen bg-[var(--bg)] text-[var(--ink)] flex items-center justify-center px-6 py-20"
    >
      <div className="max-w-lg w-full border border-[var(--ink)] bg-[var(--card-bg)] p-10 text-center">
        <span className="label-caps text-[10px] text-[var(--ink)]/60">
          Bare Essentials &bull; Skincare Refined
        </span>

        {phase === "verifying" && (
          <div data-testid="payment-verifying-state">
            <h1 className="serif-head text-3xl text-[var(--ink)] mt-4 mb-3">
              Confirming your order
            </h1>
            <p className="text-xs text-[var(--ink)]/70 font-light">
              We are verifying your payment with Stripe. This takes a few seconds.
            </p>
          </div>
        )}

        {phase === "paid" && (
          <div data-testid="payment-confirmed-state">
            <h1 className="serif-head text-3xl text-[var(--ink)] mt-4 mb-3">
              Order confirmed.
            </h1>
            <p className="text-xs text-[var(--ink)]/70 font-light mb-6">
              Thank you. Your spray ships from our Canadian workshop with tracking
              {order?.customer_email ? ` — confirmation sent to ${order.customer_email}` : ""}.
            </p>
            {order && (
              <div
                data-testid="order-summary"
                className="border border-[var(--line)] p-5 text-left mb-6"
              >
                <div className="label-caps text-[9px] text-[var(--ink)]/60 mb-3">
                  Order Summary
                </div>
                <div className="space-y-2">
                  {order.items.map((item) => (
                    <div
                      key={item.lookup_key}
                      data-testid={`order-item-${item.lookup_key}`}
                      className="flex justify-between text-xs text-[var(--ink)]"
                    >
                      <span className="font-light">
                        {item.name} × {item.quantity}
                      </span>
                      <span className="mono-num">
                        ${((item.unit_amount * item.quantity) / 100).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-[var(--line)] mt-3 pt-3 flex justify-between text-xs">
                  <span className="label-caps text-[9px] text-[var(--ink)]/70">Total</span>
                  <span data-testid="order-total" className="mono-num font-semibold">
                    ${order.amount?.toFixed(2)} {order.currency?.toUpperCase()}
                  </span>
                </div>
              </div>
            )}
            <Link
              to="/"
              data-testid="back-to-store-link"
              className="inline-block border border-[var(--ink)] bg-[var(--ink)] text-[var(--bg)] px-8 py-3 label-caps text-[10px] hover:bg-transparent hover:text-[var(--ink)] transition-colors"
            >
              Back to the Store
            </Link>
          </div>
        )}

        {phase === "timeout" && (
          <div data-testid="payment-timeout-state">
            <h1 className="serif-head text-3xl text-[var(--ink)] mt-4 mb-3">
              Still processing
            </h1>
            <p className="text-xs text-[var(--ink)]/70 font-light mb-6">
              Your payment is taking longer than expected to confirm. If you were charged, your
              order is safe — please check your email for the Stripe receipt.
            </p>
            <Link
              to="/"
              data-testid="back-to-store-link"
              className="inline-block border border-[var(--ink)] px-8 py-3 label-caps text-[10px] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors"
            >
              Back to the Store
            </Link>
          </div>
        )}

        {phase === "invalid" && (
          <div data-testid="payment-invalid-state">
            <h1 className="serif-head text-3xl text-[var(--ink)] mt-4 mb-3">Order not found</h1>
            <p className="text-xs text-[var(--ink)]/70 font-light mb-6">
              We could not locate this checkout session.
            </p>
            <Link
              to="/"
              data-testid="back-to-store-link"
              className="inline-block border border-[var(--ink)] px-8 py-3 label-caps text-[10px] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors"
            >
              Back to the Store
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
